import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Building2, MapPin, Users, Eye, Edit, Trash2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { apartmentService } from '../../services/api';

interface ApartmentPhoto {
  id: number;
  url: string;
  is_principale?: boolean;
}

interface Apartment {
  id: number;
  titre: string;
  adresse: string;
  ville: string;
  capacite: number;
  prix_par_nuit: number;
  status: string;
  created_at: string;
  owner_name?: string;
  owner_email?: string;
  photo_principale?: ApartmentPhoto;
  photos?: ApartmentPhoto[];
}

const AdminApartmentsPage: React.FC = () => {
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchApartments = async () => {
    setLoading(true);
    try {
      const response = await apartmentService.getMyApartments();
      if (response.data?.apartments) {
        setApartments(response.data.apartments);
      }
    } catch (error: any) {
      console.error('Erreur chargement appartements:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApartments();
  }, []);

  const handleDelete = async (apartmentId: number) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet appartement ?')) return;
    try {
      await apartmentService.delete(apartmentId);
      await fetchApartments();
    } catch (error: any) {
      console.error('Erreur suppression:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" /> Actif</span>;
      case 'draft':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><Clock className="w-3 h-3" /> Brouillon</span>;
      case 'inactive':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3" /> Inactif</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

  const getMainPhoto = (apt: Apartment) => {
    if (apt.photo_principale) return apt.photo_principale;
    if (apt.photos && apt.photos.length > 0) {
      const principale = apt.photos.find(p => p.is_principale);
      return principale || apt.photos[0];
    }
    return null;
  };

  const filteredApartments = apartments.filter(apt => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = 
      apt.titre.toLowerCase().includes(search) ||
      apt.ville.toLowerCase().includes(search) ||
      apt.adresse.toLowerCase().includes(search) ||
      (apt.owner_name && apt.owner_name.toLowerCase().includes(search));
    
    const matchesStatus = !statusFilter || apt.status?.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">Tous les Appartements</h1>
          <p className="text-neutral-600">{apartments.length} appartement{apartments.length > 1 ? 's' : ''} sur la plateforme</p>
        </div>
        <button onClick={fetchApartments} className="btn btn-outline flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="card p-4 flex flex-col md:flex-row gap-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher par titre, ville, adresse ou propriétaire..."
          className="flex-1 border border-gray-300 rounded-lg p-3"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg p-3"
        >
          <option value="">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="draft">Brouillon</option>
          <option value="inactive">Inactif</option>
        </select>
      </div>

      {/* Liste des appartements */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      ) : filteredApartments.length === 0 ? (
        <div className="card p-12 text-center">
          <Building2 className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">
            {searchTerm || statusFilter ? 'Aucun appartement trouvé pour ces critères' : 'Aucun appartement sur la plateforme'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredApartments.map((apartment) => (
            <div key={apartment.id} className="card p-4 flex flex-col md:flex-row gap-4">
              {/* Image */}
              <div className="w-full md:w-48 h-32 bg-neutral-200 rounded-lg overflow-hidden flex-shrink-0">
                {(() => {
                  const mainPhoto = getMainPhoto(apartment);
                  if (mainPhoto) {
                    return (
                      <img
                        src={`${baseUrl}${mainPhoto.url}`}
                        alt={apartment.titre}
                        className="w-full h-full object-cover"
                      />
                    );
                  }
                  return (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building2 className="w-12 h-12 text-neutral-400" />
                    </div>
                  );
                })()}
              </div>

              {/* Infos */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-lg truncate">{apartment.titre}</h3>
                  {getStatusBadge(apartment.status)}
                </div>
                <div className="space-y-1 text-sm text-neutral-600">
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {apartment.adresse}, {apartment.ville}
                  </p>
                  <p className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {apartment.capacite} personne{apartment.capacite > 1 ? 's' : ''} max
                  </p>
                  <p className="font-semibold text-primary-600">
                    {formatPrice(apartment.prix_par_nuit)} / nuit
                  </p>
                  {apartment.owner_name && (
                    <p className="text-xs text-neutral-400">
                      Propriétaire: {apartment.owner_name} ({apartment.owner_email})
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex md:flex-col gap-2 justify-end">
                <Link
                  to={`/apartments/${apartment.id}`}
                  className="btn btn-outline btn-sm flex items-center gap-1"
                >
                  <Eye className="w-4 h-4" />
                  Voir
                </Link>
                <Link
                  to={`/admin/apartments/${apartment.id}/edit`}
                  className="btn btn-outline btn-sm flex items-center gap-1"
                >
                  <Edit className="w-4 h-4" />
                  Modifier
                </Link>
                <button
                  onClick={() => handleDelete(apartment.id)}
                  className="btn bg-red-100 text-red-700 hover:bg-red-200 btn-sm flex items-center gap-1"
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminApartmentsPage;
